import { AxiosRequestConfig } from "axios";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import useRequest from "./useRequest";
import useCache from "../useCache";
import { shouldRequest } from "src/utils/callApi";

export const useResponse = <ResponseType>({
  requestFunction,
  values,
  requestNothingOnNull = false,
  dependency,
  extraCacheKey = "",
}: {
  requestFunction: (...args: any[]) => AxiosRequestConfig<any>;
  values?: Record<string, any>;
  requestNothingOnNull?: boolean;
  dependency?: any[] | any;
  extraCacheKey?: string;
}): {
  rows: ResponseType | null;
  setNewValue: Dispatch<SetStateAction<ResponseType | null>>;
  abort: () => void;
  loading: boolean;
  status: string | number | null;
} => {
  const requestFunctionValues = Object.values(values ?? {});
  const originalRequest = requestFunction(...requestFunctionValues);

  const [result, setRequest, abort, loading, status] = useRequest();
  const response = (result?.Items as ResponseType) ?? (result as ResponseType);

  const [cachedResponse, setCachedResponse] = useCache<ResponseType>(
    originalRequest?.url+extraCacheKey,
  );

  const responseSigRef = useRef<string | null>(null);
  const computeSig = (v: any): string => {
    if (v === null || v === undefined) return String(v);
    if (typeof v !== "object") return String(v);
    try {
      return JSON.stringify(v);
    } catch {
      return Object.prototype.toString.call(v);
    }
  };

  useEffect(() => {
    if (response == null) return;

    const nextSig = computeSig(response);
    if (nextSig === responseSigRef.current) return;

    responseSigRef.current = nextSig;
    setCachedResponse(response);
  }, [response, setCachedResponse]);

  useEffect(() => {
    if (shouldRequest(requestNothingOnNull, requestFunctionValues)) {
      setRequest(requestFunction(...requestFunctionValues));
    }
  }, [...(Array.isArray(dependency) ? dependency : [dependency])]);

  return {
    rows: (cachedResponse ?? null) as ResponseType | null,
    setNewValue: setCachedResponse,
    abort,
    loading,
    status,
  };
};

export default useResponse;

export function useResponseLegacy<ResponseType>(
  requestFunction: (...args: any[]) => AxiosRequestConfig<any>,
  values?: Record<string, any>,
  requestNothingOnNull: boolean = false,
  dependency?: any[] | any
): [ResponseType | null, Dispatch<SetStateAction<ResponseType | null>>, () => void] {
  const { rows: result, setNewValue, abort } = useResponse<ResponseType>({
    requestFunction,
    values,
    requestNothingOnNull,
    dependency,
  });
  return [result, setNewValue, abort];
}
